import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Form, Button, Container, Row, Col, Spinner, InputGroup } from "react-bootstrap";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useAuthStore } from "../model";
import { register, verify } from "../api";
import PhoneInput from "react-phone-input-2";
import { getUserByToken } from "@/entities/user/api";
import { PasswordInputGroup, VerificationCodeInput } from "@/shared/ui";

export const RegisterForm = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [numberPhone, setNumberPhone] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showVerification, setShowVerification] = useState(false);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setUser, setIsAuthorization } = useAuthStore();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showVerification && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [showVerification, timer]);

  const isValidPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    return digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidated(true);

    if (!firstName || !lastName || !numberPhone || !password || !repeatPassword) {
      enqueueSnackbar("Пожалуйста, заполните все поля.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!isValidPhone(numberPhone)) {
      enqueueSnackbar("Введите корректный номер телефона.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (password !== repeatPassword) {
      enqueueSnackbar("Пароли не совпадают.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);
    try {
      const digits = numberPhone.replace(/\D/g, "").replace(/^7/, "8");
      const data = await register({ firstName, lastName, phone: digits, password });

      if (data.success) {
        setShowVerification(true);
        setTimer(60);
        enqueueSnackbar("Введите код подтверждения.", { variant: "info" });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>, code: string) => {
    e.preventDefault();

    if (code.length !== 4) {
      enqueueSnackbar("Введите полный 4-значный код.", { variant: "error" });
      return;
    }

    const digits = numberPhone.replace(/\D/g, "").replace(/^7/, "8");

    try {
      setLoading(true);
      const data = await verify(digits, +code);

      if (data.success) {
        localStorage.setItem("token", data.result);
        setIsAuthorization();
        navigate("/");
        enqueueSnackbar("Регистрация успешна!", { variant: "success" });

        const me = await getUserByToken(data.result);
        if (me) setUser(me.result);
      } else {
        enqueueSnackbar(data.error || "Неверный код подтверждения", { variant: "error" });
      }
    } catch (err) {
      console.error("Verification error:", err);
      enqueueSnackbar("Ошибка при подтверждении кода", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="max-w-sm mx-auto">
      <Row className="justify-content-md-center mt-5">
        {showVerification ? (
          <Col xs={12} md={5} lg={4}>
            <h2 className="text-center mb-4">Верификация</h2>
            <VerificationCodeInput
              onVerify={handleVerify}
              onBack={() => {
                setShowVerification(false);
                setValidated(false);
              }}
              backTitle="Назад к регистрации"
              loading={loading}
              timer={timer}
              onResend={() => setTimer(60)}
            />
          </Col>
        ) : (
          <Col xs={12} md={5} lg={4}>
            <h2 className="text-center mb-4">Регистрация</h2>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="formFirstName" className="mb-3">
                <Form.Label>Имя</Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="Введите имя"
                  className="rounded-5"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  isInvalid={validated && !firstName}
                />
              </Form.Group>

              <Form.Group controlId="formLastName" className="mb-3">
                <Form.Label>Фамилия</Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="Введите фамилию"
                  className="rounded-5"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  isInvalid={validated && !lastName}
                />
              </Form.Group>

              <Form.Group controlId="formPhone" className="mb-3">
                <Form.Label>Номер телефона</Form.Label>
                <PhoneInput
                  country={"ru"}
                  disableDropdown={true}
                  specialLabel=""
                  masks={{ ru: "(...) ...-..-.." }}
                  onlyCountries={["ru"]}
                  placeholder="Введите номер телефона"
                  value={numberPhone}
                  onChange={(phone) => {
                    if (phone.startsWith("8")) {
                      setNumberPhone(`7${phone.slice(1)}`);
                    } else {
                      setNumberPhone(phone);
                    }
                  }}
                  isValid={validated ? isValidPhone(numberPhone) : true}
                  inputClass={`form-control rounded-5 ${validated && !isValidPhone(numberPhone) ? 'is-invalid' : ''}`}
                />
                <Form.Control.Feedback type="invalid">
                  Введите корректный номер телефона.
                </Form.Control.Feedback>
              </Form.Group>

            <PasswordInputGroup
              label="Пароль"
              value={password}
              name="password"
              onChange={(e) => setPassword(e.target.value)}
              showPassword={showPassword}
              toggleShow={() => setShowPassword(!showPassword)}
              placeholder="Введите пароль"
              isInvalid={validated && !password}
              feedback="Обязательное поле"
            />

            <PasswordInputGroup
              label="Повторите пароль"
              value={repeatPassword}
              name="repeatPassword"
              onChange={(e) => setRepeatPassword(e.target.value)}
              showPassword={showRepeatPassword}
              toggleShow={() => setShowRepeatPassword(!showRepeatPassword)}
              placeholder="Повторите пароль"
              isInvalid={validated && (repeatPassword !== password || !repeatPassword)}
              feedback="Пароли не совпадают"
            />
              <Button
                ref={buttonRef}
                variant="outline-secondary"
                type="submit"
                className="w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Регистрация...
                  </>
                ) : (
                  "Зарегистрироваться"
                )}
              </Button>
            </Form>
          </Col>
        )}
      </Row>
    </Container>
  );
};
